<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue';

  import { Shimmer } from '@ui/components/ai-elements/shimmer';

  import successGif from '@/assets/lil-stuut/success.gif';
  import thinkingGif from '@/assets/lil-stuut/thinking.gif';

  const props = defineProps<{
    isProcessing: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'done'): void;
  }>();

  const thinkingMessages = [
    'Looking at current account state',
    'Evaluating open questions',
    'Reviewing communication history',
    'Analyzing payment timeline',
    'Cross-referencing partner context',
    'Weighing response options',
    'Considering tone and phrasing',
    'Checking recent promises',
    'Assessing dispute signals',
    'Drafting next best action',
    'Synthesizing recommendation',
  ];

  const thinkingMessageIndex = ref(0);
  const currentThinkingMessage = computed(
    () => thinkingMessages[thinkingMessageIndex.value % thinkingMessages.length],
  );

  let thinkingInterval: ReturnType<typeof setInterval> | null = null;
  let doneTimeout: ReturnType<typeof setTimeout> | null = null;

  const startThinkingRotation = () => {
    if (thinkingInterval) return;
    thinkingInterval = setInterval(() => {
      thinkingMessageIndex.value += 1;
    }, 5000);
  };

  const stopThinkingRotation = () => {
    if (thinkingInterval) {
      clearInterval(thinkingInterval);
      thinkingInterval = null;
    }
  };

  watch(
    () => props.isProcessing,
    (isProc, wasProc) => {
      if (isProc) {
        if (doneTimeout) {
          clearTimeout(doneTimeout);
          doneTimeout = null;
        }
        startThinkingRotation();
      } else if (wasProc) {
        stopThinkingRotation();
        doneTimeout = setTimeout(() => emit('done'), 2000);
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    stopThinkingRotation();
    if (doneTimeout) clearTimeout(doneTimeout);
  });
</script>

<template>
  <div class="flex flex-col items-center justify-center gap-y-6 h-full w-full">
    <img v-if="isProcessing" :src="thinkingGif" alt="Thinking" class="w-40 h-40 object-contain" />
    <img v-else :src="successGif" alt="Done" class="w-40 h-40 object-contain" />

    <Shimmer v-if="isProcessing">
      <span class="text-sm font-bold">{{ currentThinkingMessage }}</span>
    </Shimmer>
  </div>
</template>
